import React, { useCallback, useEffect, useState } from "react";
import { OriginSummary } from "../../lib/origin.jsx";
import { Plus, ShieldCheck, Eye, Loader2 } from "lucide-react";
import { SectionHeader } from "../../components/Primitives";
import { useRole } from "../../lib/role.jsx";
import { listServices, notifyMapConfigChanged } from "../../lib/integrationsApi";
import MapProviderPanel from "../../components/apikeys/MapProviderPanel";
import ServiceCard from "../../components/apikeys/ServiceCard";
import EditKeyModal from "../../components/apikeys/EditKeyModal";
import CustomKeyModal from "../../components/apikeys/CustomKeyModal";
import KeyAuditLog from "../../components/apikeys/KeyAuditLog";
import { cn } from "../../lib/utils";

export default function ApiKeysAdmin() {
  const { user } = useRole();
  const canEdit = user?.role === "it_head";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cat, setCat] = useState("mapping");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [auditKey, setAuditKey] = useState(0);

  const load = useCallback(async () => {
    try { setError(null); setData(await listServices(user)); } catch (e) { setError(e.message); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); setAuditKey(k => k + 1); notifyMapConfigChanged(); };
  const visible = data ? data.services.filter(s => s.category === cat) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="api-keys-page">
      <SectionHeader
        eyebrow="Admin Console · Integrations"
        title="API Keys & Integrations"
        description="One encrypted vault for every third-party credential. Values are Fernet-encrypted in MongoDB, shown masked, and every change is written to an audit trail."
        action={canEdit && (
          <button data-testid="add-custom-key-btn" onClick={() => setAdding(true)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold bg-primary text-primary-fg rounded-full hover:opacity-90 transition-opacity">
            <Plus size={13} /> Add custom key
          </button>
        )}
      />

      <OriginSummary variant="system" />

      <div data-testid="api-keys-access-banner"
           className={cn("text-[11.5px] border rounded-sm px-3 py-2 flex items-center gap-2",
             canEdit ? "border-success/30 bg-success/5 text-success" : "border-info/30 bg-info/5 text-info")}>
        {canEdit ? <ShieldCheck size={13} /> : <Eye size={13} />}
        {canEdit ? "IT Head · full edit access. Keys are never shown in full after saving."
                 : "Read-only · masked status view. Only the IT Head can add, test or change keys."}
      </div>

      {error && <div data-testid="api-keys-error" className="text-[12px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-3">{error}</div>}
      {!data && !error && <div className="flex items-center gap-2 text-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading vault…</div>}

      {data && (
        <>
          <MapProviderPanel services={data.services} active={data.map_provider} canEdit={canEdit} actor={user} onChanged={refresh} />

          <div className="flex flex-wrap items-center gap-2" data-testid="api-keys-categories">
            {data.categories.map(c => {
              const items = data.services.filter(s => s.category === c.id && !s.free);
              const n = items.filter(s => s.configured).length;
              return (
                <button key={c.id} data-testid={`api-keys-cat-${c.id}`} onClick={() => setCat(c.id)}
                        className={cn("px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border transition-colors",
                          cat === c.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted hover:text-fg hover:border-primary/60")}>
                  {c.label} <span className="font-mono-tabular opacity-70 ml-1">{n}/{items.length}</span>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4" data-testid="api-keys-grid">
            {visible.map(s => (
              <ServiceCard key={s.service} service={s} canEdit={canEdit} actor={user} onEdit={setEditing} onChanged={refresh} />
            ))}
            {visible.length === 0 && (
              <div data-testid="api-keys-empty" className="tile p-6 text-[12px] text-muted md:col-span-2">
                No keys in this category yet.{canEdit && " Use “Add custom key” to store one."}
              </div>
            )}
          </div>

          <KeyAuditLog actor={user} refreshKey={auditKey} />
        </>
      )}

      {editing && <EditKeyModal service={editing} actor={user} onClose={() => setEditing(null)} onSaved={refresh} />}
      {adding && <CustomKeyModal categories={data?.categories || []} defaultCategory={cat === "mapping" ? "other" : cat}
                                 actor={user} onClose={() => setAdding(false)} onSaved={refresh} />}
    </div>
  );
}
